import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import bankService from "../../../services/bank.service";
import "../../../styles/users.css";

const initial = {
  bankName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  gstNumber: "",
  panNumber: "",
  branchName: "",
  adminName: "",
  adminPassword: "",
  adminBranchName: "",
  adminEmployeeNumber: "",
  status: "pending_payment",
};

export default function SsdiCreateBank() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState(null);
  const [registrationId, setRegistrationId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => setResendCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const upperFields = new Set(["gstNumber", "panNumber"]);
    setForm((p) => ({
      ...p,
      [name]: upperFields.has(name) ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.adminPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await bankService.ssdiCreateBank(form);
      const body = res?.data ?? res;
      const payload = body?.data && typeof body.data === "object" ? body.data : body;
      const nextRegistrationId = String(payload?.registrationId || "").trim();
      if (!nextRegistrationId) {
        setError("OTP was sent but the registration session was not returned. Please try again.");
        return;
      }
      setRegistrationId(nextRegistrationId);
      setResendCooldown(Number(payload?.resendAvailableInSeconds || 60));
      setOtpSent(Boolean(body?.requiresOtp || nextRegistrationId));
      return;
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await bankService.verifySsdiCreateBankOtp(registrationId, otp);
      const data = res?.data?.data;
      setCreds({
        email: data?.admin?.email,
        password: form.adminPassword,
        bankCode: data?.bank?.bankCode,
      });
      setForm(initial);
      setOtpSent(false);
      setOtp("");
      setRegistrationId("");
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || resendCooldown > 0) return;
    setLoading(true);
    setError("");
    try {
      const res = await bankService.resendSsdiCreateBankOtp(registrationId);
      setResendCooldown(Number(res?.data?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initial);
    setError("");
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
  };

  const backFromOtp = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  return (
    <div className="page create-company-page repo-form-page ssdi-create-bank-page">
      <div className="create-company-header">
        <div>
          <h2>Create Bank</h2>
        </div>
        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/ssdi/banks")}
        >
          Back to Banks
        </button>
      </div>

      {creds && (
        <div className="created-credentials-card ssdi-create-bank-credentials">
          <h3>Bank Created — Login Credentials</h3>
          <p>
            <strong>Bank Code:</strong> {creds.bankCode}
          </p>
          <p>
            <strong>Email:</strong> {creds.email}
          </p>
          <p>
            <strong>Password:</strong> {creds.password}
          </p>
          <button
            type="button"
            className="primary-page-btn"
            onClick={() => navigate("/ssdi/banks")}
          >
            Back to Banks List
          </button>
        </div>
      )}

      {!otpSent ? <form className="create-company-form" onSubmit={handleSubmit}>
        <div className="create-company-card">
          <section
            className="create-company-section"
            aria-labelledby="ssdi-bank-section-details"
          >
            <header
              className="create-company-section__head"
              id="ssdi-bank-section-details"
            >
              <h3>Bank details</h3>
            </header>
            <div className="form-grid two-column">
              <div className="form-group">
                <label htmlFor="bankName">Bank Name *</label>
                <input
                  id="bankName"
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  required
                  placeholder="Bank name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="pending_payment">Pending</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="Bank email"
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="Phone number"
                  autoComplete="tel"
                />
              </div>
              <div className="form-group">
                <label htmlFor="gstNumber">GST Number</label>
                <input
                  id="gstNumber"
                  name="gstNumber"
                  value={form.gstNumber}
                  onChange={handleChange}
                  placeholder="e.g. 22AAAAA0000A1Z5"
                  maxLength={15}
                  autoCapitalize="characters"
                />
              </div>
              <div className="form-group">
                <label htmlFor="panNumber">PAN Number</label>
                <input
                  id="panNumber"
                  name="panNumber"
                  value={form.panNumber}
                  onChange={handleChange}
                  placeholder="e.g. AAAAA9999A"
                  maxLength={10}
                  autoCapitalize="characters"
                />
              </div>
              <div className="form-group">
                <label htmlFor="branchName">Branch</label>
                <input
                  id="branchName"
                  name="branchName"
                  value={form.branchName}
                  onChange={handleChange}
                  placeholder="Head office / primary branch"
                />
              </div>
            </div>
          </section>

          <section
            className="create-company-section"
            aria-labelledby="ssdi-bank-section-location"
          >
            <header
              className="create-company-section__head"
              id="ssdi-bank-section-location"
            >
              <h3>Location</h3>
            </header>
            <div className="form-grid two-column">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  id="state"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  placeholder="State"
                />
              </div>
              <div className="form-group full-width">
                <label htmlFor="address">Address</label>
                <input
                  id="address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Bank address"
                />
              </div>
            </div>
          </section>

          <section
            className="create-company-section create-company-section--last"
            aria-labelledby="ssdi-bank-section-admin"
          >
            <header
              className="create-company-section__head"
              id="ssdi-bank-section-admin"
            >
              <h3>Bank admin account</h3>
            </header>
            <div className="form-grid two-column">
              <div className="form-group">
                <label htmlFor="adminName">Admin Name *</label>
                <input
                  id="adminName"
                  name="adminName"
                  value={form.adminName}
                  onChange={handleChange}
                  required
                  placeholder="Bank admin full name"
                  autoComplete="name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="adminPassword">Admin Password * (min 8)</label>
                <input
                  id="adminPassword"
                  type="password"
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                  placeholder="Create password"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="adminBranchName">Admin branch</label>
                <input
                  id="adminBranchName"
                  name="adminBranchName"
                  value={form.adminBranchName}
                  onChange={handleChange}
                  placeholder={form.branchName || "Branch for bank admin"}
                />
              </div>
              <div className="form-group">
                <label htmlFor="adminEmployeeNumber">Admin employee number</label>
                <input
                  id="adminEmployeeNumber"
                  name="adminEmployeeNumber"
                  value={form.adminEmployeeNumber}
                  onChange={handleChange}
                  placeholder="Employee / staff ID"
                />
              </div>
            </div>
          </section>
        </div>

        {error && <p className="error-text create-company-error">{error}</p>}

        <div className="create-company-actions create-company-actions--sticky">
          <button
            type="button"
            className="secondary-page-btn"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </button>
          <button
            type="submit"
            className="primary-page-btn"
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Bank"}
          </button>
        </div>
      </form> : (
        <section className="create-company-card">
          <h3>Verify Bank Phone</h3>
          <p className="muted">We sent a 6-digit OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.</p>
          <div className="form-group">
            <label htmlFor="ssdi-bank-otp">Enter OTP</label>
            <input id="ssdi-bank-otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" />
          </div>
          {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
          {error && <p className="error-text">{error}</p>}
          <div className="create-company-actions">
            <button type="button" className="secondary-page-btn" onClick={backFromOtp} disabled={loading}>Back</button>
            <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>Resend OTP</button>
            <button type="button" className="primary-page-btn" onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}>{loading ? "Verifying..." : "Verify OTP"}</button>
          </div>
        </section>
      )}
    </div>
  );
}
